import React, { useState, useRef, useEffect } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

interface SearchableDropdownProps {
  label: string;
  value: string;
  allLabel: string;
  searchPlaceholder: string;
  options: string[];
  formatOption: (option: string) => string;
  onSelect: (value: string) => void;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  label,
  value,
  allLabel,
  searchPlaceholder,
  options,
  formatOption,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = searchTerm
    ? options.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  return (
    <div className="relative" ref={ref}>
      <label className="block text-xs eg-text-secondary mb-1">{label}</label>
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-3 py-2 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg eg-text-primary text-sm text-left focus:outline-none focus:border-engramiq-green hover:border-engramiq-green transition-all flex items-center justify-between"
        >
          <span className="truncate">{value ? formatOption(value) : allLabel}</span>
          {isOpen ? (
            <ChevronUp className="w-4 h-4 eg-text-secondary flex-shrink-0 ml-2" />
          ) : (
            <ChevronDown className="w-4 h-4 eg-text-secondary flex-shrink-0 ml-2" />
          )}
        </button>
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg shadow-lg z-50 max-h-60 overflow-hidden flex flex-col">
            <div className="p-2 border-b border-[var(--color-border)]">
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-2 py-1 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded eg-text-primary text-xs focus:outline-none focus:border-engramiq-green"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="overflow-y-auto">
              <button
                onClick={() => {
                  onSelect('');
                  setIsOpen(false);
                  setSearchTerm('');
                }}
                className="w-full px-3 py-2 text-left text-xs hover:bg-[var(--color-surface-hover)] transition-colors eg-text-primary"
              >
                {allLabel}
              </button>
              {filteredOptions.map(option => (
                <button
                  key={option}
                  onClick={() => {
                    onSelect(option);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full px-3 py-2 text-left text-xs hover:bg-[var(--color-surface-hover)] transition-colors ${
                    value === option ? 'bg-engramiq-green/20 eg-text-primary' : 'eg-text-primary'
                  }`}
                >
                  {formatOption(option)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableDropdown;
